import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { 
  getWeddingById, 
  addTable, 
  updateTable, 
  deleteTable,
  addMember,
  updateMember,
  deleteMember
} from '../../firebase/firestore';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Container,
  Tooltip,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as BackIcon,
  PictureAsPdf as PdfIcon,
  Person as PersonIcon,
  Share as ShareIcon,
  QrCode as QrCodeIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  TextSnippet as TextSnippetIcon
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode.react';

const WeddingDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { weddingId } = useParams();
  const { userData, currentUser } = useAuth();
  
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Table dialog state
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [tableToEdit, setTableToEdit] = useState(null);
  const [tableFormData, setTableFormData] = useState({ tableNumber: '' });
  
  // Member dialog state
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [memberToEdit, setMemberToEdit] = useState(null);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [memberFormData, setMemberFormData] = useState({ name: '' });
  const [bulkMembersDialogOpen, setBulkMembersDialogOpen] = useState(false);
  const [bulkMembersText, setBulkMembersText] = useState('');
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState('');
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // PDF export state
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingTxt, setExportingTxt] = useState(false);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchWedding = async () => {
      try {
        const weddingData = await getWeddingById(weddingId);
        
        if (!weddingData) {
          setError(t('common.notFound'));
          return;
        }
        
        setWedding(weddingData);
      } catch (err) {
        console.error('Error fetching wedding:', err);
        setError(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchWedding();
  }, [weddingId, t]);

  // Handle table dialog
  const handleOpenTableDialog = (tableIndex = null) => {
    if (tableIndex !== null) {
      const table = wedding.tables[tableIndex];
      setTableToEdit(tableIndex);
      setTableFormData({ tableNumber: table.tableNumber });
    } else {
      setTableToEdit(null);
      setTableFormData({ tableNumber: '' });
    }
    setTableDialogOpen(true);
  };

  const handleCloseTableDialog = () => {
    setTableDialogOpen(false);
    setTableToEdit(null);
    setTableFormData({ tableNumber: '' });
  };

  const handleTableFormChange = (e) => {
    const { name, value } = e.target;
    setTableFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTableSubmit = async () => {
    try {
      if (!tableFormData.tableNumber.trim()) {
        setError(t('tables.numberRequired'));
        return;
      }

      if (tableToEdit !== null) {
        // Update existing table
        await updateTable(weddingId, tableToEdit, {
          tableNumber: tableFormData.tableNumber,
          members: wedding.tables[tableToEdit].members || []
        });
        
        // Update local state
        const updatedTables = [...wedding.tables];
        updatedTables[tableToEdit] = {
          ...updatedTables[tableToEdit],
          tableNumber: tableFormData.tableNumber
        };
        
        setWedding({ ...wedding, tables: updatedTables });
        setSuccess(t('tables.updateSuccess'));
      } else {
        // Add new table
        const newTable = {
          tableNumber: tableFormData.tableNumber,
          members: []
        };
        
        await addTable(weddingId, newTable);
        
        // Refresh wedding data to get the updated tables array
        const updatedWedding = await getWeddingById(weddingId);
        setWedding(updatedWedding);
        setSuccess(t('tables.createSuccess'));
      }
      
      handleCloseTableDialog();
    } catch (error) {
      console.error('Error saving table:', error);
      setError(t('common.error'));
    }
  };

  // Handle member dialog
  const handleOpenMemberDialog = (tableIndex, memberIndex = null) => {
    setSelectedTableId(tableIndex);
    
    if (memberIndex !== null) {
      const member = wedding.tables[tableIndex].members[memberIndex];
      setMemberToEdit(memberIndex);
      setMemberFormData({ name: member });
    } else {
      setMemberToEdit(null);
      setMemberFormData({ name: '' });
    }
    
    setMemberDialogOpen(true);
  };

  const handleCloseMemberDialog = () => {
    setMemberDialogOpen(false);
    setMemberToEdit(null);
    setSelectedTableId(null);
    setMemberFormData({ name: '' });
  };

  const handleMemberFormChange = (e) => {
    const { name, value } = e.target;
    setMemberFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMemberSubmit = async () => {
    try {
      if (!memberFormData.name.trim()) {
        setError(t('members.nameRequired'));
        return;
      }

      if (memberToEdit !== null) {
        // Update existing member
        await updateMember(weddingId, selectedTableId, memberToEdit, memberFormData.name);
        
        // Update local state
        const updatedTables = [...wedding.tables];
        updatedTables[selectedTableId].members[memberToEdit] = memberFormData.name;
        
        setWedding({ ...wedding, tables: updatedTables });
        setSuccess(t('members.updateSuccess'));
      } else {
        // Add new member
        await addMember(weddingId, selectedTableId, memberFormData.name);
        
        // Refresh wedding data to get the updated tables array
        const updatedWedding = await getWeddingById(weddingId);
        setWedding(updatedWedding);
        setSuccess(t('members.createSuccess'));
      }
      
      handleCloseMemberDialog();
    } catch (error) {
      console.error('Error saving member:', error);
      setError(t('common.error'));
    }
  };

  // Handle delete dialog
  const handleOpenDeleteDialog = (type, itemIndex, tableIndex = null) => {
    setDeleteType(type);
    setItemToDelete({ index: itemIndex, tableIndex });
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteType('');
    setItemToDelete(null);
  };

  const handleDelete = async () => {
    try {
      if (deleteType === 'table') {
        // Delete table
        await deleteTable(weddingId, itemToDelete.index);
        
        // Refresh wedding data to get the updated tables array
        const updatedWedding = await getWeddingById(weddingId);
        setWedding(updatedWedding);
        setSuccess(t('tables.deleteSuccess'));
      } else if (deleteType === 'member') {
        // Delete member
        await deleteMember(weddingId, itemToDelete.tableIndex, itemToDelete.index);
        
        // Update local state
        const updatedTables = [...wedding.tables];
        updatedTables[itemToDelete.tableIndex].members.splice(itemToDelete.index, 1);
        
        setWedding({ ...wedding, tables: updatedTables });
        setSuccess(t('members.deleteSuccess'));
      }
      
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting item:', error);
      setError(t('common.error'));
    }
  };

  // Handle PDF export
  const handleExportPdf = async () => {
    if (!wedding) return;
    
    try {
      setExportingPdf(true);
      
      // Create PDF with UTF-8 support
      const pdf = new jsPDF('p', 'pt', 'a4');
      
      // Add Roboto font
      pdf.addFont('https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Me5Q.ttf', 'Roboto', 'normal');
      pdf.addFont('https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlvAw.ttf', 'Roboto', 'bold');
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 40;
      let yPosition = 40;
      const lineHeight = 20;
      
      // Set default font
      pdf.setFont('Roboto');
      
      // Add title
      pdf.setFontSize(24);
      pdf.setFont('Roboto', 'bold');
      pdf.text(wedding.name, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += lineHeight * 2;
      
      // Add date and location
      pdf.setFontSize(14);
      pdf.setFont('Roboto', 'normal');
      pdf.text(`${t('weddings.location')}: ${wedding.location}`, margin, yPosition);
      yPosition += lineHeight;
      pdf.text(`${t('weddings.date')}: ${wedding.date ? new Date(wedding.date.toDate()).toLocaleDateString() : ''}`, margin, yPosition);
      yPosition += lineHeight * 2;
      
      // Add tables and members
      wedding.tables?.forEach((table, index) => {
        // Check if we need a new page
        if (yPosition > pdf.internal.pageSize.getHeight() - 40) {
          pdf.addPage();
          yPosition = 40;
        }
        
        // Table header
        pdf.setFontSize(16);
        pdf.setFont('Roboto', 'bold');
        pdf.text(`${t('tables.tableNumber')} ${table.tableNumber}`, margin, yPosition);
        yPosition += lineHeight;
        
        // Table members
        pdf.setFontSize(12);
        pdf.setFont('Roboto', 'normal');
        if (table.members && table.members.length > 0) {
          table.members.forEach(member => {
            // Check if we need a new page
            if (yPosition > pdf.internal.pageSize.getHeight() - 40) {
              pdf.addPage();
              yPosition = 40;
            }
            pdf.text(`• ${member}`, margin + 20, yPosition);
            yPosition += lineHeight;
          });
        } else {
          pdf.text(t('tables.noMembers'), margin + 20, yPosition);
          yPosition += lineHeight;
        }
        
        yPosition += lineHeight; // Add space between tables
      });
      
      // Save PDF
      pdf.save(`${wedding.name}_seating_plan.pdf`);
      setSuccess(t('common.exportSuccess'));
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError(t('common.error'));
    } finally {
      setExportingPdf(false);
    }
  };

  // Handle TXT export
  const handleExportTxt = async () => {
    if (!wedding) return;
    
    try {
      setExportingTxt(true);
      
      // Create text content
      let content = `${wedding.name}\n\n`;
      content += `${t('weddings.location')}: ${wedding.location}\n`;
      content += `${t('weddings.date')}: ${wedding.date ? new Date(wedding.date.toDate()).toLocaleDateString() : ''}\n\n`;
      
      // Add tables and members
      wedding.tables?.forEach((table) => {
        content += `${t('tables.tableNumber')} ${table.tableNumber}\n`;
        if (table.members && table.members.length > 0) {
          table.members.forEach(member => {
            content += `• ${member}\n`;
          });
        } else {
          content += t('tables.noMembers') + '\n';
        }
        content += '\n';
      });
      
      // Create and download the text file
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${wedding.name}_seating_plan.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess(t('common.exportSuccess'));
    } catch (err) {
      console.error('Error exporting TXT:', err);
      setError(t('common.error'));
    } finally {
      setExportingTxt(false);
      handleCloseExportMenu();
    }
  };

  const handleOpenExportMenu = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportAnchorEl(null);
  };

  const handleBack = () => {
    navigate('/weddings');
  };

  const handleShare = () => {
    setShareDialogOpen(true);
  };

  const handleShowQR = () => {
    setQrDialogOpen(true);
  };

  const handleCopyLink = () => {
    const guestLink = `${window.location.origin}/wedding/${weddingId}/guest`;
    navigator.clipboard.writeText(guestLink);
    setSnackbar({
      open: true,
      message: t('common.linkCopied'),
      severity: 'success'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleBulkMembersSubmit = async () => {
    try {
      if (!bulkMembersText.trim()) {
        setError(t('members.nameRequired'));
        return;
      }

      // Split text by newlines and filter out empty lines
      const members = bulkMembersText
        .split('\n')
        .map(member => member.trim())
        .filter(member => member.length > 0);

      if (members.length === 0) {
        setError(t('members.nameRequired'));
        return;
      }

      // Update the table with all members
      const updatedTables = [...wedding.tables];
      updatedTables[selectedTableId].members = [
        ...updatedTables[selectedTableId].members || [],
        ...members
      ];

      await updateTable(weddingId, selectedTableId, {
        tableNumber: updatedTables[selectedTableId].tableNumber,
        members: updatedTables[selectedTableId].members
      });

      // Update local state
      setWedding({ ...wedding, tables: updatedTables });
      setSuccess(t('members.createSuccess'));
      setBulkMembersDialogOpen(false);
      setBulkMembersText('');
    } catch (error) {
      console.error('Error adding members:', error);
      setError(t('common.error'));
    }
  };

  const handleOpenBulkMembersDialog = (tableIndex) => {
    setSelectedTableId(tableIndex);
    setBulkMembersText('');
    setBulkMembersDialogOpen(true);
  };

  const handleCloseBulkMembersDialog = () => {
    setBulkMembersDialogOpen(false);
    setBulkMembersText('');
    setSelectedTableId(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!wedding) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {t('common.notFound')}
      </Alert>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            {wedding.name}
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenTableDialog()}
              sx={{ mr: 2 }}
            >
              {t('tables.create')}
            </Button>
            <Tooltip title={t('common.share')}>
              <IconButton onClick={handleShare}>
                <ShareIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('common.qrCode')}>
              <IconButton onClick={handleShowQR}>
                <QrCodeIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('common.export')}>
              <IconButton 
                onClick={handleOpenExportMenu}
                disabled={exportingPdf || exportingTxt}
              >
                {exportingPdf || exportingTxt ? (
                  <CircularProgress size={24} />
                ) : (
                  <DownloadIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" component="div">
                {t('weddings.location')}: {wedding.location}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" component="div">
                {t('weddings.date')}: {wedding.date ? new Date(wedding.date.toDate()).toLocaleDateString() : ''}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Typography variant="h5" sx={{ mb: 3 }}>
          {t('tables.title')}
        </Typography>

        {wedding.tables?.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('weddings.noTables')}
          </Alert>
        ) : (
          <div id="wedding-tables">
            <Grid container spacing={3}>
              {wedding.tables?.map((table, tableIndex) => (
                <Grid item xs={12} sm={6} md={4} key={tableIndex}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="div">
                          {t('tables.tableNumber')}: {table.tableNumber}
                        </Typography>
                        <Box>
                          <IconButton 
                            color="secondary" 
                            onClick={() => handleOpenTableDialog(tableIndex)}
                            title={t('common.edit')}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => handleOpenDeleteDialog('table', tableIndex)}
                            title={t('common.delete')}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ mb: 2 }} />
                      
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        {t('members.title')} ({table.members?.length || 0})
                      </Typography>
                      
                      {table.members?.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {t('tables.noMembers')}
                        </Typography>
                      ) : (
                        <List dense>
                          {table.members?.map((memberName, memberIndex) => (
                            <ListItem key={memberIndex}>
                              <PersonIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                              <ListItemText primary={memberName} />
                              <ListItemSecondaryAction>
                                <IconButton 
                                  edge="end" 
                                  size="small"
                                  onClick={() => handleOpenMemberDialog(tableIndex, memberIndex)}
                                  title={t('common.edit')}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  edge="end" 
                                  size="small"
                                  onClick={() => handleOpenDeleteDialog('member', memberIndex, tableIndex)}
                                  title={t('common.delete')}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </CardContent>
                    <CardActions>
                      <Button 
                        size="small" 
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenMemberDialog(tableIndex)}
                      >
                        {t('members.create')}
                      </Button>
                      <Button 
                        size="small" 
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenBulkMembersDialog(tableIndex)}
                      >
                        {t('members.addMultiple')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </div>
        )}

        {/* Table Dialog */}
        <Dialog open={tableDialogOpen} onClose={handleCloseTableDialog}>
          <DialogTitle>
            {tableToEdit ? t('tables.edit') : t('tables.create')}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="tableNumber"
              label={t('tables.tableNumber')}
              type="text"
              fullWidth
              value={tableFormData.tableNumber}
              onChange={handleTableFormChange}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseTableDialog}>{t('common.cancel')}</Button>
            <Button onClick={handleTableSubmit} color="primary">
              {t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Member Dialog */}
        <Dialog open={memberDialogOpen} onClose={handleCloseMemberDialog}>
          <DialogTitle>
            {memberToEdit ? t('members.edit') : t('members.create')}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label={t('members.name')}
              type="text"
              fullWidth
              value={memberFormData.name}
              onChange={handleMemberFormChange}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMemberDialog}>{t('common.cancel')}</Button>
            <Button onClick={handleMemberSubmit} color="primary">
              {t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
          <DialogTitle>{t('common.confirmAction')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {deleteType === 'table' 
                ? t('tables.confirmDelete') 
                : t('members.confirmDelete')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>{t('common.cancel')}</Button>
            <Button onClick={handleDelete} color="error">
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Share Dialog */}
        <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)}>
          <DialogTitle>{t('common.share')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              value={`${window.location.origin}/wedding/${weddingId}/guest`}
              margin="normal"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton onClick={handleCopyLink}>
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShareDialogOpen(false)}>{t('common.close')}</Button>
          </DialogActions>
        </Dialog>

        {/* QR Code Dialog */}
        <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)}>
          <DialogTitle>{t('qrCode.title')}</DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <QRCode 
              value={`${window.location.origin}/wedding/${weddingId}/guest`}
              size={256}
              level="H"
            />
            <Typography variant="body2" sx={{ mt: 2 }}>
              {t('qrCode.scan')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setQrDialogOpen(false)}>{t('common.close')}</Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Members Dialog */}
        <Dialog 
          open={bulkMembersDialogOpen} 
          onClose={handleCloseBulkMembersDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{t('members.addMultiple')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t('members.addMultipleDescription')}
            </DialogContentText>
            <TextField
              autoFocus
              multiline
              rows={10}
              margin="dense"
              label={t('members.names')}
              fullWidth
              value={bulkMembersText}
              onChange={(e) => setBulkMembersText(e.target.value)}
              placeholder={t('members.namesPlaceholder')}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBulkMembersDialog}>{t('common.cancel')}</Button>
            <Button onClick={handleBulkMembersSubmit} color="primary">
              {t('common.add')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Export Menu */}
        <Menu
          anchorEl={exportAnchorEl}
          open={Boolean(exportAnchorEl)}
          onClose={handleCloseExportMenu}
        >
          <MenuItem onClick={() => {
            handleExportPdf();
            handleCloseExportMenu();
          }}>
            <ListItemIcon>
              <PdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>PDF</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            handleExportTxt();
            handleCloseExportMenu();
          }}>
            <ListItemIcon>
              <TextSnippetIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>TXT</ListItemText>
          </MenuItem>
        </Menu>
      </Paper>
    </Container>
  );
};

export default WeddingDetails;
